import React, { Fragment } from 'react'
import { injectIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import MediaQuery from 'react-responsive'

import Spinner from '@uniiverse/library/components/spinner'
import Pagination from '@uniiverse/library/components/pagination'
import Button from '@uniiverse/library/components/button'

import FilterIcon from '@uniiverse/library/assets/icons/filter.svg'
import SearchIcon from '@uniiverse/library/assets/icons/new-search.svg'

import { SearchInputs } from 'src/components/SearchInputs/SearchInputs'
import { TextInput } from 'src/components/text-input/index'
import { EventCard, MOBILE_EVENT_IMAGE_HEIGHT, MOBILE_EVENT_IMAGE_WIDTH } from 'src/components/event-card/index'

import { ToggleableQuery } from 'src/lib/toggleable-query'
import { BREAK_MD } from 'src/lib/media-queries'
import { asyncComponent } from 'src/lib/async-component'
import { formattedDateTimeRange, formattedPriceRange, formattedDateStartTime } from 'src/lib/formatter'
import { trackAmplitude } from 'src/lib/analytics'
import { MAIN_CATEGORIES } from 'src/lib/categories'
import { AppContext } from 'src/AppContext'
import { setDigitalData, googleTagManagerEvent } from 'src/lib/digital-data'
import searchInputsLocale from 'src/components/SearchInputs/locale'

import { DATE_DISPLAY_OPTIONS } from '../../components/calendar/constants'

import locale from './locale'

import eventsQuery from './explore.gql'

import style from './index.css'

const Navbar = asyncComponent(() => (
  import(/* webpackChunkName: "components-navbar", webpackPreload: true */ 'src/components/Navbar/Navbar')
), 'Navbar')

const Filters = asyncComponent(() => (
  import(/* webpackChunkName: "components-search" */ 'src/components/filters/index')
), 'Filters')

const Search = asyncComponent(() => (
  import(/* webpackChunkName: "components-search" */ 'src/components/search/index')
), 'Search')

const SearchFilter = asyncComponent(() => (
  import(/* webpackChunkName: "components-search-filter" */ 'src/components/search-filter/index')
), 'SearchFilter')

const Footer = asyncComponent(() => (
  import(/* webpackChunkName: "components-footer", webpackPrefetch: true */ 'src/components/Footer/Footer')
), 'Footer')

const EVENT_COUNT = window.innerWidth >= BREAK_MD ? 24 : 10
const ENUM_BY_CATEGORY = {
  business: 'BUSINESS',
  comedy: 'COMEDY',
  crafts: 'CRAFTS',
  fashion: 'FASHION',
  film: 'FILM',
  'food-drink': 'FOOD_DRINK',
  'galleries-museums': 'GALLERIES_MUSEUMS',
  music: 'MUSIC',
  'performing-arts': 'PERFORMING_ARTS',
  social: 'SOCIAL',
  sports: 'SPORTS',
  tech: 'TECH',
  other: 'OTHER',
}
const ENUM_BY_TIME = {
  today: 'TODAY',
  'this-week': 'THIS_WEEK',
  'this-weekend': 'THIS_WEEKEND',
  'next-week': 'NEXT_WEEK',
}
const PRICE_FREE = 'free'
// eslint-disable-next-line max-len
const NO_EVENTS_IMAGE = 'https://www.universe.com/multiverse/assets/images/templates/explore/no-events-found-6db902d371583589e5a17932195c9a4c.png'

class ExploreComponent extends React.Component {
  static stateFromUrl() {
    const { searchParams } = new URL(window.location)
    const [lat, lon] = decodeURIComponent(searchParams.get('ll')).split(',')

    return {
      searchQuery: decodeURIComponent(searchParams.get('query') || ''),
      locationQuery: decodeURIComponent(searchParams.get('loc') || ''),
      lat: parseFloat(lat) || SSR_DATA.lat,
      lon: parseFloat(lon) || SSR_DATA.lon,
      category: decodeURIComponent(searchParams.get('categories') || ''),
      time: decodeURIComponent(searchParams.get('time') || ''),
      price: decodeURIComponent(searchParams.get('price') || ''),
      currentPage: parseInt(decodeURIComponent(searchParams.get('page') || ''), 10) || 1,
    }
  }

  static trackRouteView({ locationQuery, data, viewer, viewerFetched }) {
    if (!viewerFetched) return

    const eventData = {}
    const { searchParams } = new URL(window.location)
    const categories = searchParams.get('categories')
    const time = searchParams.get('time')
    eventData.city = locationQuery
    if (categories) eventData.category = categories
    if (time) eventData.when = time
    eventData.search_city = locationQuery
    eventData.events_count = (data && data.events.totalCount) || 0
    trackAmplitude('Explore View', eventData, viewer)
    googleTagManagerEvent({ event: 'virtualPageView' })
  }

  constructor(props) {
    super(props)
    this.state = {
      searchOpened: false,
      searchFilterOpened: false,
      filtersOpened: false,
      searchSectionId: '',
      ...this.constructor.stateFromUrl(),
    }
  }

  componentDidMount() {
    window.scrollTo(0, 0)
  }

  componentDidUpdate() {
    if (!this.state.searchOpened && !this.state.searchFilterOpened) {
      this.rehydrateStateFromUrl()
    }
  }

  rehydrateStateFromUrl() {
    const currentState = {
      searchQuery: this.state.searchQuery,
      locationQuery: this.state.locationQuery,
      lat: this.state.lat,
      lon: this.state.lon,
      category: this.state.category,
      time: this.state.time,
      price: this.state.price,
      currentPage: this.state.currentPage,
    }
    const newState = this.constructor.stateFromUrl()

    if (JSON.stringify(currentState) !== JSON.stringify(newState)) {
      this.setState(newState)
    }
  }

  toggleSearch = (props = {}) => {
    this.setState({ searchOpened: !this.state.searchOpened, searchSectionId: (props.sectionId || 'search_bar') })
  }

  toggleSearchFilter = () => {
    this.setState({ searchFilterOpened: !this.state.searchFilterOpened })
  }

  toggleFilters = () => {
    this.setState({ filtersOpened: !this.state.filtersOpened })
  }

  isFilterActive() {
    return this.state.category || this.state.time || this.state.price
  }

  seeAll = () => {
    this.setState({
      searchQuery: '',
      category: '',
      time: '',
      price: '',
    })
  }

  resetFilters = () => {
    const { searchParams } = new URL(window.location)
    searchParams.set('categories', '')
    searchParams.set('time', '')
    searchParams.set('price', '')
    const url = `/explore?${searchParams.toString()}`
    this.props.history.push(url)
    this.resetPagination()
    if (this.state.filtersOpened) this.toggleFilters()
  }

  resetPagination = () => {
    this.setState({ currentPage: 1 })
  }

  searchInput() {
    const { formatMessage } = this.props.intl

    return (
      <div>
        <TextInput
          icon={
            <SearchIcon className={style.searchIcon} />
          }
          endIcon={
            <FilterIcon
              onClick={this.toggleSearchFilter}
              className={`${style.filterIcon} ${this.isFilterActive() ? style.highlightedIcon : ''}`}
            />
          }
          inputProps={{
            value: this.state.searchQuery,
            placeholder: formatMessage(searchInputsLocale.searchPlaceholder),
            className: style.searchInput,
            onClick: this.toggleSearch,
          }}
        />
      </div>
    )
  }

  header({ locationQuery }) {
    const { formatMessage } = this.props.intl

    return (
      <div className={style.header}>
        <div className={style.headerContent}>
          <SearchInputs
            searchText={<SearchIcon className={style.searchIcon} />}
            searchQuery={this.state.searchQuery}
            locationQuery={locationQuery}
            lat={this.state.lat}
            lon={this.state.lon}
            className={style.searchInputs}
            queryClassName={style.querySearchInputs}
            locationClassName={style.locationSearchInputs}
            buttonClassName={style.buttonSearchInputs}
            page="explore"
            history={this.props.history}
            onSearch={this.resetPagination}
          />
          <div className={style.headerButtons}>
            <div className={`${style.filtersButton} ${this.state.filtersOpened ? style.pressedFiltersButton : ''}`}>
              <Button onClick={this.toggleFilters} type="primary">
                <FilterIcon className={style.filterIcon} />
                <span>{formatMessage(locale.filters)}</span>
              </Button>
            </div>
            <div className={style.resetButton}>
              <button className={style.resetFilters} disabled={!this.isFilterActive()} onClick={this.resetFilters}>
                {formatMessage(locale.resetFilters)}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  filters(viewer) {
    return (
      <Filters
        category={this.state.category}
        time={this.state.time}
        price={this.state.price}
        close={this.toggleFilters}
        history={this.props.history}
        viewer={viewer}
        onFilter={this.resetPagination}
        opened={this.state.filtersOpened}
      />
    )
  }

  noEvents({ locationQuery }) {
    const { formatMessage } = this.props.intl
    const ll = encodeURIComponent(`${this.state.lat},${this.state.lon}`)
    const loc = encodeURIComponent(locationQuery)
    const url = `/explore?ll=${ll}&loc=${loc}`

    return [
      <div className={style.noEvents}>
        <img src={NO_EVENTS_IMAGE} alt={formatMessage(locale.noEvents)} />
        <h1>{formatMessage(locale.noEvents, { cityName: locationQuery })}</h1>
        <h2>{formatMessage(locale.noEventsDescription)}</h2>
        {
          this.state.searchQuery || this.isFilterActive() ?
            <Link to={url} href={url}>
              <Button onClick={this.seeAll} type="primary">{formatMessage(locale.seeAllEvents)}</Button>
            </Link>
            :
            null
        }
      </div>,
    ]
  }

  pagination(response) {
    const { data, fetchMore } = response

    if (!data || !data.events || data.events.totalCount <= EVENT_COUNT) {
      return null
    }

    const onPageChange = ({ selected }) => {
      const { searchParams } = new URL(window.location)
      searchParams.set('page', selected + 1)
      this.props.history.push(`/explore?${searchParams.toString()}`)

      fetchMore({
        variables: {
          latitude: this.state.lat,
          longitude: this.state.lon,
          limit: EVENT_COUNT,
          query: this.state.searchQuery ? this.state.searchQuery : null,
          categories: this.state.category ? [ENUM_BY_CATEGORY[this.state.category]] : null,
          time: this.state.time ? ENUM_BY_TIME[this.state.time] : null,
          freeEventsOnly: this.state.price === PRICE_FREE ? true : null,
          page: selected + 1,
          width: MOBILE_EVENT_IMAGE_WIDTH,
          height: MOBILE_EVENT_IMAGE_HEIGHT,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return fetchMoreResult
        },
      })
      this.setState({ currentPage: selected + 1 })
      window.scrollTo(0, 0)
    }

    return (
      <div className={style.pagination}>
        <MediaQuery maxWidth={BREAK_MD - 1}>
          <Pagination
            showLabel
            showElipsis={false}
            currentPage={this.state.currentPage}
            pageCount={Math.ceil(data.events.totalCount / EVENT_COUNT)}
            pageRangeDisplayed={1}
            marginPagesDisplayed={1}
            onPageChange={onPageChange}
          />
        </MediaQuery>
        <MediaQuery minWidth={BREAK_MD}>
          <Pagination
            showLabel
            showElipsis
            currentPage={this.state.currentPage}
            pageCount={Math.ceil(data.events.totalCount / EVENT_COUNT)}
            pageRangeDisplayed={2}
            marginPagesDisplayed={1}
            onPageChange={onPageChange}
          />
        </MediaQuery>
      </div>
    )
  }

  eventCards(data) {
    return data.events.events.map((event) => {
      const { startAt, endAt } = event.timeSlots.nodes[0]
      const { totalCount } = event.timeSlots
      const { intl } = this.props
      const date = event.dateDisplayOption === DATE_DISPLAY_OPTIONS.startTimeOnly
        ? formattedDateStartTime({
          startAt,
          intl,
        })
        : formattedDateTimeRange({
          startAt,
          endAt,
          totalCount,
          intl,
        })

      return (
        <EventCard
          key={event.id}
          className={style.eventCard}
          coverImage={event.coverPhoto}
          address={event.geo.address}
          date={date}
          title={event.title}
          url={`${event.source.url}?ref=universe-discover`}
          hostUrl={`${event.host.url}?ref=universe-discover`}
          hostName={event.host.name}
          price={formattedPriceRange({
            minPrice: event.minPrice,
            maxPrice: event.maxPrice,
            currency: event.currency,
            intl: this.props.intl,
          })}
          page="explore"
          slug={event.slug}
          type={event.type}
          hiddenDate={event.hiddenDate}
          hiddenPrice={event.hiddenPrice}
          dateDisplayOption={event.dateDisplayOption}
        />
      )
    })
  }

  events({ response, locationQuery }) {
    const { data } = response
    const { intl } = this.props
    const { formatMessage } = intl
    const searchLocation = (
      <Fragment>
        <MediaQuery maxWidth={BREAK_MD - 1}>
          <span
            className={style.cityName}
            onClick={() => this.toggleSearch({ sectionId: 'city_name' })}
            onKeyPress={() => this.toggleSearch({ sectionId: 'city_name' })}
            role="button"
            tabIndex="-1"
          >
            {locationQuery}
          </span>
        </MediaQuery>
        <MediaQuery minWidth={BREAK_MD}>
          {locationQuery}
        </MediaQuery>
      </Fragment>
    )

    let events

    let humanQuery = ''
    if (this.state.searchQuery) {
      humanQuery = `'${this.state.searchQuery}' `
    } else if (this.state.category) {
      const category = MAIN_CATEGORIES.find(c => c.value === this.state.category)
      humanQuery = category ? `${formatMessage(category.locale)} ` : ''
    }

    let eventCount = 0

    if (!response.loading && !response.error && data && data.events) {
      eventCount = data.events.totalCount
      events = <div className={style.events}>{this.eventCards(data)}</div>
    } else {
      events = <div className={style.spinner}><Spinner type="circle" /></div>
    }

    return (
      <Fragment>
        <div className={style.eventsContent}>
          <h1 className={style.title}>
            {formatMessage(locale.exploreEvents, { eventCount, query: humanQuery })}&nbsp;{searchLocation}
          </h1>
          {events}
          {this.pagination(response)}
        </div>
      </Fragment>
    )
  }

  render() {
    return (
      <AppContext.Consumer>
        {({ viewer, viewerFetched }) => {
          const locationQuery = this.state.locationQuery || SSR_DATA.cityName

          setDigitalData({
            channel: 'search-results',
            type: 'Search Results Page',
            country: SSR_DATA.countryCode,
          })

          if (this.state.searchOpened) {
            return (
              <Search
                closeSearch={this.toggleSearch}
                searchQuery={this.state.searchQuery}
                locationQuery={locationQuery}
                lat={this.state.lat}
                lon={this.state.lon}
                history={this.props.history}
                viewer={viewer}
                referral="explore"
                page="explore"
                sectionId={this.state.searchSectionId}
              />
            )
          }

          if (this.state.searchFilterOpened) {
            return (
              <SearchFilter
                closeSearchFilter={this.toggleSearchFilter}
                category={this.state.category}
                time={this.state.time}
                price={this.state.price}
                history={this.props.history}
                viewer={viewer}
              />
            )
          }

          return (
            <ToggleableQuery
              query={eventsQuery}
              key={eventsQuery}
              variables={{
                latitude: this.state.lat,
                longitude: this.state.lon,
                limit: EVENT_COUNT,
                query: this.state.searchQuery ? this.state.searchQuery : null,
                page: this.state.currentPage,
                categories: this.state.category ? [ENUM_BY_CATEGORY[this.state.category]] : null,
                time: this.state.time ? ENUM_BY_TIME[this.state.time] : null,
                freeEventsOnly: this.state.price === PRICE_FREE ? true : null,
                width: MOBILE_EVENT_IMAGE_WIDTH,
                height: MOBILE_EVENT_IMAGE_HEIGHT,
              }}
              onCompleted={data => this.constructor.trackRouteView({ locationQuery, data, viewer, viewerFetched })}
            >
              {response => (
                <div className={style.container}>
                  <Navbar sticky />
                  <MediaQuery maxWidth={BREAK_MD - 1}>
                    {this.searchInput()}
                  </MediaQuery>
                  <MediaQuery minWidth={BREAK_MD}>
                    {this.header({ locationQuery })}
                    {this.filters(viewer)}
                  </MediaQuery>
                  {
                    response.data && response.data.events && response.data.events.events.length === 0 ?
                      this.noEvents({ locationQuery })
                      :
                      this.events({ response, locationQuery })
                  }
                  <Footer />
                </div>
              )}
            </ToggleableQuery>
          )
        }}
      </AppContext.Consumer>
    )
  }
}

export const Explore = injectIntl(ExploreComponent)
